import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowDownIcon } from '@shared/components/icons/svg-icon.tsx';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import ScreensDialogForm from '@modules/digital-signage/components/screens-dialog/screens-dialog-form.tsx';
import PlaylistsDialogForm from '@modules/digital-signage/components/playlists-dialog/playlists-dialog-form.tsx';
import useScreensStore from '@modules/digital-signage/pages/device-screens/store/screens.store.ts';
import usePlaylistsStore from '@modules/digital-signage/pages/playlists/store/playlists.store.ts';
import useGroupSettingsStore from '@modules/digital-signage/components/group-settings/store/group-settings.store.ts';
import DefaultContentSelect from '@modules/digital-signage/components/group-settings/components/default-screen-or-playlist/components/default-content-select/default-content-select.tsx';
import {
  AssignedEntityEnum,
  DefaultContentOption,
  GroupSettingsFormValues
} from '@modules/digital-signage/model/group-settings.model.ts';
import {
  getDefaultContentValue,
  toAssignedValues,
  toDefaultContentOptions
} from '@modules/digital-signage/components/group-settings/utils/group-settings.utils.ts';
import {
  DigitalSignageScreenReqModel,
  DigitalSignageScreensModel
} from '@modules/digital-signage/model/screens.model.ts';
import {
  PlaylistModel,
  PlaylistReqModel
} from '@modules/digital-signage/model/playlists.model.ts';

const DefaultScreenOrPlaylist: React.FC = () => {
  const { t } = useTranslation();
  const isTabletScreen = useMediaQuery('(max-width: 1715px)');
  const { control, setValue } = useFormContext<GroupSettingsFormValues>();

  const { resetAllSelectedMedia } = useMediaLibraryStore();
  const { createRow } = useScreensStore();
  const { createPlaylist } = usePlaylistsStore();
  const {
    screensOptions,
    playlistsOptions,
    getScreensOptions,
    getPlaylistsOptions,
    isScreensLoading,
    isPlaylistsLoading
  } = useGroupSettingsStore();

  const [openScreenDialog, setOpenScreenDialog] = useState<boolean>(false);
  const [openPlaylistDialog, setOpenPlaylistDialog] = useState<boolean>(false);
  const [screenPrefill, setScreenPrefill] = useState<string>('');
  const [playlistPrefill, setPlaylistPrefill] = useState<string>('');

  const watchedScreen = useWatch({ control, name: 'assignedScreen' });
  const watchedPlaylist = useWatch({ control, name: 'assignedPlaylist' });

  const contentValue = useMemo(
    () => getDefaultContentValue(watchedScreen, watchedPlaylist),
    [watchedScreen, watchedPlaylist]
  );
  const contentOptions = useMemo(
    () => toDefaultContentOptions(screensOptions, playlistsOptions),
    [screensOptions, playlistsOptions]
  );

  const screenPrefillItem = useMemo(
    () =>
      screenPrefill
        ? ({ title: screenPrefill } as DigitalSignageScreensModel)
        : null,
    [screenPrefill]
  );
  const playlistPrefillItem = useMemo(
    () =>
      playlistPrefill ? ({ name: playlistPrefill } as PlaylistModel) : null,
    [playlistPrefill]
  );

  const handleContentChange = (option: DefaultContentOption | null) => {
    const { assignedScreen, assignedPlaylist } = toAssignedValues(option);
    const options = { shouldValidate: true, shouldDirty: true };

    setValue('assignedScreen', assignedScreen, options);
    setValue('assignedPlaylist', assignedPlaylist, options);
  };

  const handleCreateContent = (title: string, type: AssignedEntityEnum) => {
    if (type === AssignedEntityEnum.Screen) {
      setScreenPrefill(title);
      setOpenScreenDialog(true);
      return;
    }
    setPlaylistPrefill(title);
    setOpenPlaylistDialog(true);
  };

  const handleCloseScreenDialog = () => {
    resetAllSelectedMedia();
    setOpenScreenDialog(false);
    setScreenPrefill('');
  };

  const handleClosePlaylistDialog = () => {
    setOpenPlaylistDialog(false);
    setPlaylistPrefill('');
  };

  const handleCreateScreen = async (data: DigitalSignageScreenReqModel) => {
    try {
      const created = await createRow({
        data,
        handleClose: handleCloseScreenDialog
      });
      if (!created) return;

      await getScreensOptions();

      handleContentChange({
        id: created._id,
        title: created.title,
        value: created.title,
        type: AssignedEntityEnum.Screen
      });
    } catch {
      // The store already surfaced the error through the snackbar.
    }
  };

  const handleCreatePlaylist = async (body: PlaylistReqModel) => {
    const created = await createPlaylist({
      body,
      handleClose: handleClosePlaylistDialog
    });
    if (!created) return;

    await getPlaylistsOptions();

    handleContentChange({
      id: created._id,
      title: created.name,
      value: created.name,
      type: AssignedEntityEnum.Playlist
    });
  };

  return (
    <>
      <Accordion
        className="bordered-accordion"
        disableGutters
        elevation={0}
        square
        defaultExpanded
      >
        <AccordionSummary
          expandIcon={<ArrowDownIcon />}
          aria-controls="default-screen-or-playlist-content"
          id="default-screen-or-playlist-header"
        >
          <Typography
            variant="subtitle4"
            className="text-(--dark-1)! max-lg-xl:text-[16px]!"
          >
            {t('digital_signage_default_screen_or_playlist')}
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <div className="flex flex-col gap-3">
            <DefaultContentSelect
              value={contentValue}
              options={contentOptions}
              loading={isScreensLoading() || isPlaylistsLoading()}
              onChange={handleContentChange}
              onCreate={handleCreateContent}
            />

            <Typography
              variant="caption"
              className="text-(--dark-1)! font-normal! bg-(--primary-100) rounded-xl p-3 opacity-80 border border-(--gray-9)"
            >
              {t('digital_signage_default_content_hint')}
            </Typography>
          </div>
        </AccordionDetails>
      </Accordion>

      <AppDialog
        open={openScreenDialog}
        title={t('digital_signage_add_screens_title')}
        size={isTabletScreen ? 'sm' : 'md'}
        content={
          <ScreensDialogForm
            selectedItem={screenPrefillItem}
            onSave={handleCreateScreen}
          />
        }
        onClose={handleCloseScreenDialog}
      />

      <AppDialog
        open={openPlaylistDialog}
        title={t('digital_signage_add_playlist_title')}
        size={isTabletScreen ? 'md' : 'lg'}
        content={
          <PlaylistsDialogForm
            selectedItem={playlistPrefillItem}
            screensData={screensOptions}
            onSave={handleCreatePlaylist}
          />
        }
        onClose={handleClosePlaylistDialog}
      />
    </>
  );
};

export default DefaultScreenOrPlaylist;
